import { API_BASE_URL } from "../../constant/constant";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { useTranslation } from "react-i18next";

import {
  SidebarContainer,
  Header,
  LogoIcon,
  Nav,
  NavItem,
  ItemImg,
  ItemText,
  ToggleButton,
  DividerDiv,
  UserDiv,
  UserText,
  More,
  Popup,
  PopupItem,
  ExpandButton,
  Counter,
} from "./Style";

import GenSearchSideBar from "./GenSearchSideBar";
import { handleClearChat } from "../../utils/utils";

import Logo from "../../assets/Logo.png";
import FullLogo from "../../assets/full-logo.png";
import FolderIcon from "../../assets/folder.png";
import SearchIcon from "../../assets/search.png";
import MailingIcon from "../../assets/mailing.png";
import ChatIcon from "../../assets/chat.png";
import SummaryIcon from "../../assets/summary.png";
import RecordIcon from "../../assets/record.png";
import Divider from "../../assets/divider.svg";
import Support from "../../assets/support.png";
import SmallDivider from "../../assets/divider-small.svg";
import ArrowLeft from "../../assets/arrow-left.svg";
import UserIcon from "../../assets/user.png";
import DownArrowIcon from "../../assets/down-arrow.svg";
import ForestIcon from "../../assets/forest.png";
import TodoIcon from "../../assets/Todo.jpg";
import CalendarIcon from "../../assets/calendar.png";
import AiIcon from "../../assets/ai-icon.png";
import IntegrationsIcon from "../../assets/integrations.png";
import WorkflowIcon from "../../assets/workflow.png";
import StorageIcon from "../../assets/Storage.png";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { useSnackbar } from "../../context/SnackBarContext";

const Sidebar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  profileUpdated,
  setProfileUpdated,
  notifUpdated,
  setNotifUpdated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeNavItem, setActiveNavItem] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [userName, setUserName] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isSearchSidebarVisible, setSearchSidebarVisible] = useState(false);
  const showSnackbar = useSnackbar();

  const popupRef = useRef(null);
  const socketRef = useRef(null);
  const sidebarRef = useRef(null);

  const navItems = [
    { icon: AiIcon, text: t("sidebar.aiAssistant") },
    { icon: CalendarIcon, text: t("sidebar.assistantCalendar") },

    { icon: WorkflowIcon, text: t("sidebar.workflows") },
    { icon: RecordIcon, text: t("sidebar.skills") },
    { icon: FolderIcon, text: t("sidebar.applications") },

    { icon: TodoIcon, text: t("sidebar.todoApp") },
    { icon: RecordIcon, text: t("sidebar.meetings") },
    { icon: MailingIcon, text: t("sidebar.emailAutomations") },
    { icon: ChatIcon, text: t("sidebar.chatWithDoc") },
    { icon: SummaryIcon, text: t("sidebar.summarize") },
    { icon: SearchIcon, text: t("sidebar.generalSearch") },
    { icon: IntegrationsIcon, text: t("sidebar.integrations") },
    { icon: ForestIcon, text: t("sidebar.forestApp") },
  ];

  const routes = {
    0: "/avatar-chat",
    1: "/avatar-calendar",
    2: "/workflows",
    3: "/skills",
    4: "/",
    5: "/todo",
    6: "/transcriber",
    7: "/email",
    8: "/chat",
    9: "/summarize",
    10: "/generalsearch",
    11: "/integrations",
    12: "/forest",
    13: "/storage",
  };

  const alwaysVisibleIndexes = [0, 1, 2, 3, 4, 10, 11, 12];
  const expandableIndexes = [5, 6, 7, 8, 9];

  useEffect(() => {
    const currentPath = location.pathname;
    const index = Object.keys(routes).find((key) => routes[key] === currentPath);
    if (index !== undefined) {
      setActiveNavItem(parseInt(index));
      if (expandableIndexes.includes(parseInt(index))) {
        setIsExpanded(true);
      }
    }
  }, [location.pathname]);

  const handleNavItemClick = (index) => {
    setActiveNavItem(index);

    if (index === 13) {
      setSearchSidebarVisible(true);
      return;
    }

    if (routes[index]) {
      navigate(routes[index]);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem("id");
    if (!userId) return;

    socketRef.current = io(API_BASE_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });

    socketRef.current.emit("join", userId);

    socketRef.current.on("newNotification", (data) => {
      setNotificationCount(data.count);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const image = localStorage.getItem("image");
    setProfileImage(image || null);
    const storedName = localStorage.getItem("name");
    if (storedName) {
      setUserName(storedName);
    }

    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (profileUpdated) {
      setProfileImage(localStorage.getItem("image"));
      setUserName(localStorage.getItem("name"));
      setProfileUpdated(false);
    }
  }, [profileUpdated, setProfileUpdated]);

  useEffect(() => {
    if (notifUpdated) {
      fetchNotificationCount();
      setNotifUpdated(false);
    }
  }, [notifUpdated]);

  const fetchNotificationCount = async () => {
    try {
      const userId = localStorage.getItem("id");
      if (!userId) return;

      const response = await fetch(
        `${API_BASE_URL}/api/notification/unread-count?userId=${userId}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": `${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json();

      if (data.responseCode === 200) {
        setNotificationCount(data.count);
      }
    } catch (error) {
      console.error("Error fetching notification count:", error);
    }
  };

  const fetchGoogleDriveAuth = async () => {
    try {
      const userId = localStorage.getItem("id");
      if (!userId) return;

      const response = await fetch(
        `${API_BASE_URL}/api/integration/google?user_id=${userId}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": `${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json();

      if (data.responseCode === 200 && data.driveAuth) {
        const { access_token, refresh_token, expires_in } = data.driveAuth;

        // Only set to localStorage if all values are present and valid
        if (access_token && refresh_token && expires_in) {
          localStorage.setItem("access_token", access_token);
          localStorage.setItem("refresh_token", refresh_token);
          localStorage.setItem("expires_in", expires_in);
        }
      }
    } catch (error) {
      console.error("Error fetching Google Drive auth:", error);
    }
  };

  useEffect(() => {
    fetchNotificationCount();
    fetchGoogleDriveAuth();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    setShowPopup(false);
    navigate("/login");
  };

  const handleNotificationClick = () => {
    setActiveNavItem(11);
    navigate("/notifications");
  };

  const handleVideoGuide = () => {
    setShowPopup(false);
    navigate("/guide");
  };

  const handleUpdateProfile = () => {
    setShowPopup(false);
    navigate("/settings");
  };

  const handleBilling = () => {
    setShowPopup(false);
    navigate("/billing");
  };

  const handlenoti = () => {
    setShowPopup(false);
    navigate("/notifications");
  };

  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleGenSearchChatClear = () => {
    const userId = localStorage.getItem("id");
    handleClearChat(userId);
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  console.log("This is the username", userName);

  return (
    <>
      <SidebarContainer isOpen={isSidebarOpen}>
        <Header>
          <LogoIcon
            src={isSidebarOpen ? FullLogo : Logo}
            isCollapsed={!isSidebarOpen}
            onClick={() => navigate("/avatar-chat")}
          />
        </Header>

        <Nav>
          {navItems.map((item, index) => {
            return (
              <NavItem
                key={index}
                className={`nav-item-${index}`}
                isOpen={isSidebarOpen}
                isActive={activeNavItem === index}
                hasRoute={true}
                onClick={(e) => {
                  handleNavItemClick(index);
                  if (index === 4) {
                    e.stopPropagation();
                    setIsExpanded((prev) => !prev);
                  }
                }}
                style={{
                  display:
                    alwaysVisibleIndexes.includes(index) ||
                      (isExpanded && expandableIndexes.includes(index))
                      ? "flex"
                      : "none",
                }}
              >
                <ItemImg src={item.icon} isOpen={isSidebarOpen} />

                {isSidebarOpen && <ItemText>{item.text}</ItemText>}

                {index === 4 && isSidebarOpen && (
                  <ExpandButton onClick={toggleExpand}>
                    {isExpanded ? <IoIosArrowUp /> : <IoIosArrowDown />}
                  </ExpandButton>
                )}
              </NavItem>
            );
          })}
        </Nav>

        <DividerDiv src={isSidebarOpen ? Divider : SmallDivider} />

        <Nav>
          <NavItem
            isOpen={isSidebarOpen}
            isActive={activeNavItem === 13}
            hasRoute={true}
            onClick={() => handleNavItemClick(13)}
          >
            <ItemImg src={StorageIcon} isOpen={isSidebarOpen} />
            {isSidebarOpen && <ItemText>{t("sidebar.storage")}</ItemText>}
          </NavItem>
        </Nav>

        <Nav style={{ marginTop: "auto" }}>
          <NavItem hasRoute={true} onClick={() => navigate("/support")}>
            <ItemImg src={Support} isOpen={isSidebarOpen} />
            {isSidebarOpen && <ItemText>{t("sidebar.support")}</ItemText>}
            {/* {notificationCount > 0 && (
              <Counter isOpen={isSidebarOpen}>{notificationCount}</Counter>
            )} */}
          </NavItem>
        </Nav>

        <DividerDiv src={isSidebarOpen ? Divider : SmallDivider} />

        <UserDiv className="nav-item-settings">
          <img
            src={profileImage || UserIcon}
            alt="avatar"
            style={{ borderRadius: "50%", width: "2rem", height: "2rem" }}
          />
          <UserText isOpen={isSidebarOpen}>{userName}</UserText>
          <More
            isOpen={isSidebarOpen}
            src={DownArrowIcon}
            onClick={() => setShowPopup(!showPopup)}
            alt="arrow"
            style={{ transform: "rotate(180deg)" }}
          />
          <Popup isOpen={showPopup} ref={popupRef}>
            <PopupItem onClick={handleUpdateProfile}>
              {t("sidebar.settings")}
            </PopupItem>
            <PopupItem onClick={handleBilling}>
              {t("sidebar.billing")}
            </PopupItem>
            <PopupItem onClick={handlenoti}>
              {t("sidebar.notification")}
            </PopupItem>
            <PopupItem onClick={handleVideoGuide}>
              {t("sidebar.guide")}
            </PopupItem>
            <PopupItem onClick={handleLogout}>{t("sidebar.logout")}</PopupItem>
          </Popup>
        </UserDiv>

        <ToggleButton onClick={toggleSidebar}>
          <img
            src={ArrowLeft}
            alt="toggle"
            style={{
              transform: isSidebarOpen ? "rotate(0deg)" : "rotate(180deg)",
              // position: "absolute",
              // zIndex: "10000",
              // marginLeft: "-20px",
            }}
          />
        </ToggleButton>
      </SidebarContainer>

      {isSearchSidebarVisible && (
        <GenSearchSideBar
          ref={sidebarRef}
          onClose={() => setSearchSidebarVisible(false)}
          onUpdate={handleGenSearchChatClear}
        />
      )}
    </>
  );
};

export default Sidebar;
